import React, { useState } from "react";
import { trpc } from "../lib/trpc.js";
import {
  Wrench, Plus, Loader2, Search, Tag, FileCode,
  Trash2, Edit3, Clock, Power,
  AlertCircle, Save, BrainCircuit, FolderOpen,
} from "lucide-react";

// ─── Unified Skills Page ────────────────────────────────────────────────────
// Combina Brain Skills (read-only, do repositório) + Custom Skills (CRUD, do DB)
// em uma única interface com abas.

type TabId = "brain" | "custom";

export function Skills() {
  const [activeTab, setActiveTab] = useState<TabId>("brain");
  const [view, setView] = useState<"list" | "create" | "edit">("list");
  const [editId, setEditId] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const tabs: { id: TabId; label: string; icon: typeof Wrench }[] = [
    { id: "brain", label: "Skills do Brain", icon: BrainCircuit },
    { id: "custom", label: "Skills Personalizadas", icon: Wrench },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2.5">
            <Wrench className="w-6 h-6 text-primary" />
            Skills
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Skills do repositório Brain e skills personalizadas da organização
          </p>
        </div>
        {activeTab === "custom" && view === "list" && (
          <button
            onClick={() => setView("create")}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
          >
            <Plus className="w-4 h-4" />
            Nova Skill
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-secondary/50 rounded-lg p-1 w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              setView("list");
              setEditId(null);
              setSearch("");
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeTab === tab.id
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground hover:bg-secondary"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "brain" && <BrainSkillsSection />}

      {activeTab === "custom" && (
        <>
          {view === "list" && (
            <SkillsList
              search={search}
              onSearch={setSearch}
              onEdit={(id) => {
                setEditId(id);
                setView("edit");
              }}
            />
          )}
          {view === "create" && (
            <SkillEditor
              onBack={() => setView("list")}
              onSaved={() => setView("list")}
            />
          )}
          {view === "edit" && editId && (
            <SkillEditor
              skillId={editId}
              onBack={() => { setView("list"); setEditId(null); }}
              onSaved={() => { setView("list"); setEditId(null); }}
            />
          )}
        </>
      )}
    </div>
  );
}

// ─── Brain Skills (read-only, from repository) ─────────────────────────────
function BrainSkillsSection() {
  const { data: skills, isLoading } = trpc.brain.getSkillsWithFiles.useQuery();

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-card border border-border rounded-xl p-5 animate-pulse">
            <div className="h-5 bg-muted rounded w-1/3" />
          </div>
        ))}
      </div>
    );
  }

  if (!skills || skills.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 text-center">
        <BrainCircuit className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
        <p className="text-muted-foreground">Nenhuma skill encontrada no repositório Brain</p>
        <p className="text-xs text-muted-foreground/50 mt-1">
          Skills do Brain ficam em /skills/ no repositório ymonetize-brain
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Skills carregadas do repositório Brain (somente leitura). Para editar, modifique os arquivos no repositório.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {skills.map((skill: any) => (
          <div
            key={skill.id}
            className="bg-card border border-border rounded-xl p-5 hover:border-primary/30 transition-all"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                <BrainCircuit className="w-4 h-4 text-primary" />
              </div>
              <div>
                <h4 className="text-foreground font-medium text-sm">{skill.name}</h4>
                <span className="text-xs text-muted-foreground font-mono">{skill.id}</span>
              </div>
            </div>
            {skill.description && (
              <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{skill.description}</p>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              {skill.files.map((f: any) => (
                <span
                  key={f.name}
                  className="text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded font-mono flex items-center gap-1"
                >
                  <FolderOpen className="w-3 h-3" />
                  {f.name}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Custom Skills List (CRUD) ──────────────────────────────────────────────
function SkillsList({
  search,
  onSearch,
  onEdit,
}: {
  search: string;
  onSearch: (s: string) => void;
  onEdit: (id: string) => void;
}) {
  const { data: skills, isLoading } = trpc.customSkills.list.useQuery();
  const utils = trpc.useUtils();

  const deleteMutation = trpc.customSkills.delete.useMutation({
    onSuccess: () => utils.customSkills.list.invalidate(),
  });

  const toggleMutation = trpc.customSkills.update.useMutation({
    onSuccess: () => utils.customSkills.list.invalidate(),
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-card border border-border rounded-xl p-5 animate-pulse">
            <div className="h-5 bg-muted rounded w-1/3 mb-2" />
            <div className="h-3 bg-muted rounded w-2/3" />
          </div>
        ))}
      </div>
    );
  }

  const filtered = (skills || []).filter(
    (s: any) =>
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.slug.toLowerCase().includes(search.toLowerCase()) ||
      (s.description || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-3">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Buscar skills personalizadas..."
          className="w-full bg-card border border-border rounded-lg pl-10 pr-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <Wrench className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-muted-foreground text-sm">
            {search ? "Nenhuma skill encontrada" : "Nenhuma skill personalizada criada ainda"}
          </p>
          <p className="text-muted-foreground/60 text-xs mt-1">
            Skills personalizadas são instruções que seus agentes podem carregar para executar tarefas específicas
          </p>
        </div>
      ) : (
        filtered.map((skill: any) => (
          <div
            key={skill.id}
            className="bg-card border border-border rounded-xl p-4 hover:border-primary/20 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-foreground truncate">{skill.name}</h3>
                  <span className="text-xs px-1.5 py-0.5 rounded bg-secondary text-muted-foreground font-mono">
                    {skill.slug}
                  </span>
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded ${
                      skill.isActive
                        ? "bg-emerald-500/10 text-emerald-500"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {skill.isActive ? "Ativa" : "Inativa"}
                  </span>
                </div>
                {skill.description && (
                  <p className="text-xs text-muted-foreground mb-2 line-clamp-1">
                    {skill.description}
                  </p>
                )}
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Tag className="w-3 h-3" />
                    {skill.category}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(skill.updatedAt).toLocaleDateString("pt-BR")}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1 ml-3">
                <button
                  onClick={() =>
                    toggleMutation.mutate({ id: skill.id, isActive: !skill.isActive })
                  }
                  className={`p-2 rounded-lg transition-colors ${
                    skill.isActive
                      ? "text-emerald-500 hover:bg-emerald-500/10"
                      : "text-muted-foreground hover:bg-muted"
                  }`}
                  title={skill.isActive ? "Desativar" : "Ativar"}
                >
                  <Power className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onEdit(skill.id)}
                  className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <Edit3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    if (confirm(`Excluir skill "${skill.name}"?`)) {
                      deleteMutation.mutate({ id: skill.id });
                    }
                  }}
                  className="p-2 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ─── Skill Editor (Create/Edit) ─────────────────────────────────────────────
function SkillEditor({
  skillId,
  onBack,
  onSaved,
}: {
  skillId?: string;
  onBack: () => void;
  onSaved: () => void;
}) {
  const { data: existingSkill } = trpc.customSkills.get.useQuery(
    { id: skillId! },
    { enabled: !!skillId }
  );
  const utils = trpc.useUtils();

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [instructions, setInstructions] = useState("");
  const [category, setCategory] = useState("general");

  React.useEffect(() => {
    if (existingSkill) {
      setName(existingSkill.name);
      setSlug(existingSkill.slug);
      setDescription(existingSkill.description || "");
      setInstructions(existingSkill.instructions);
      setCategory(existingSkill.category || "general");
    }
  }, [existingSkill]);

  const createMutation = trpc.customSkills.create.useMutation({
    onSuccess: () => {
      utils.customSkills.list.invalidate();
      onSaved();
    },
  });

  const updateMutation = trpc.customSkills.update.useMutation({
    onSuccess: () => {
      utils.customSkills.list.invalidate();
      onSaved();
    },
  });

  const handleSave = () => {
    if (skillId) {
      updateMutation.mutate({
        id: skillId,
        name,
        description,
        instructions,
        category,
      });
    } else {
      createMutation.mutate({ name, slug, description, instructions, category });
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error || updateMutation.error;

  return (
    <div className="space-y-4">
      <button
        onClick={onBack}
        className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
      >
        &larr; Voltar para skills
      </button>

      <div className="bg-card border border-border rounded-xl p-6 space-y-4">
        <h2 className="text-lg font-bold text-foreground">
          {skillId ? "Editar Skill" : "Nova Skill"}
        </h2>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">Nome</label>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (!skillId) {
                  setSlug(
                    e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "-")
                      .replace(/^-|-$/g, "")
                  );
                }
              }}
              placeholder="Ex: Análise de Concorrentes"
              className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">Slug</label>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="analise-concorrentes"
              disabled={!!skillId}
              className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50"
            />
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-foreground mb-1.5 block">Descrição</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Breve descrição do que esta skill faz..."
            className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>

        <div>
          <label className="text-sm font-medium text-foreground mb-1.5 block">Categoria</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            <option value="general">Geral</option>
            <option value="marketing">Marketing</option>
            <option value="development">Desenvolvimento</option>
            <option value="analytics">Analytics</option>
            <option value="operations">Operações</option>
            <option value="finance">Finanças</option>
            <option value="hr">RH</option>
            <option value="custom">Personalizado</option>
          </select>
        </div>

        {/* Content editor */}
        <div>
          <label className="text-sm font-medium text-foreground mb-1.5 flex items-center gap-2">
            <FileCode className="w-4 h-4 text-primary" />
            Conteúdo da Skill (Markdown)
          </label>
          <div className="border border-border rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-secondary/30">
              <span className="text-xs text-muted-foreground">SKILL.md</span>
              <span className="text-xs text-muted-foreground">
                {instructions.length.toLocaleString()} chars
              </span>
            </div>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="# Nome da Skill&#10;&#10;## Quando usar&#10;Use esta skill quando...&#10;&#10;## Instruções&#10;1. Primeiro passo&#10;2. Segundo passo&#10;&#10;## Templates&#10;```&#10;Template de exemplo&#10;```"
              className="w-full min-h-[400px] p-4 bg-card text-foreground text-sm font-mono leading-relaxed resize-y focus:outline-none placeholder:text-muted-foreground/50"
            />
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-500 bg-red-500/10 p-3 rounded-lg">
            <AlertCircle className="w-4 h-4" />
            {error.message}
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            onClick={onBack}
            className="px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={isPending || !name || !slug || instructions.length < 10}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {skillId ? "Salvar Alterações" : "Criar Skill"}
          </button>
        </div>
      </div>
    </div>
  );
}
